// Analytics System - Main Entry Point
//
// This module provides the unified analytics API that replaces direct provider calls.
// It manages the event bus, provider registration, and provides a simple interface
// for tracking events across all registered analytics providers.

import { notifyFormSubmit } from '../config/PostConversionFormSubmitTracker';
import {
    getDataLayerConversionEvents,
    getFormDetectionStrategies,
    isCurrentPageCTADestination,
    passesConversionFilter,
    shouldEnableFormTracking,
} from '../config/SiteConfigManager';
import { getConfigLoadFailure } from '../config/unified/configLoadDiagnostics';
import type { VisitorProfile } from '../services/inboundx';
import { logger } from '../utils/core/logger';
import { AnalyticsEventBus } from './event-bus';
import { FormDetectionManager, type FormTrackingData } from './formDetection';
import { CookieProvider } from './providers/cookie-provider';
import type { DatabaseConfig } from './providers/database-provider';
import { DatabaseProvider } from './providers/database-provider';
import { DebugProvider } from './providers/debug-provider';
import type { EvaluationConfig } from './providers/evaluation-provider';
import { EvaluationProvider } from './providers/evaluation-provider';
import { LocalStorageProvider } from './providers/localstorage-provider';
import type { ConversationConfig } from './providers/n8n-conversation-provider';
import { N8nConversationProvider } from './providers/n8n-conversation-provider';
import type { ParentWindowConfig } from './providers/parent-window-provider';
import { ParentWindowProvider } from './providers/parent-window-provider';
import { PostHogProvider } from './providers/posthog-provider';
import { SentryProvider } from './providers/sentry-provider';
import type { AnalyticsConfig, AnalyticsContext, EventCategory } from './types';
import { EventCategories, EventTypes, type MessageSourceType } from './types';

// Global analytics event bus instance
let eventBus: AnalyticsEventBus | null = null;
let posthogProvider: PostHogProvider | null = null;
let sentryProvider: SentryProvider | null = null;
let n8nProvider: N8nConversationProvider | null = null;
let evaluationProvider: EvaluationProvider | null = null;
let debugProvider: DebugProvider | null = null;
let databaseProvider: DatabaseProvider | null = null;
let localStorageProvider: LocalStorageProvider | null = null;
let cookieProvider: CookieProvider | null = null;
let parentWindowProvider: ParentWindowProvider | null = null;

/**
 * Initialize the analytics system with configuration and register all providers
 */
export async function initializeAnalytics(
    config: AnalyticsConfig & {
        conversation?: ConversationConfig;
        evaluation?: EvaluationConfig;
        database?: DatabaseConfig;
        enableFormTracking?: boolean; // Auto-enable form detection and tracking
    } = {}
): Promise<void> {
    // Expected: AnalyticsProvider's useEffect may re-fire when its dependencies change
    // (e.g. apiVersion, environment, inboundXService) or when the widget re-inits on
    // SPA URL changes (forceInit: true in main.tsx). The singleton guard is intentional
    // — silently return without re-initializing the event bus and providers.
    // However, form tracking must be re-initialized if it was cleaned up by
    // AnalyticsContext's useEffect cleanup (which nulls formDetectionManager).
    if (eventBus) {
        logger.debug('Analytics system already initialized');
        // Re-initialize form tracking if it was cleaned up but analytics is still alive
        if (config.enableFormTracking && !formDetectionManager) {
            logger.debug('🔍 Re-enabling form tracking after cleanup');
            setupFormTracking({
                domain: config.globalContext?.domain,
            });
        }
        return;
    }

    logger.debug('🚀 Initializing analytics system with config:', {
        hasGlobalContext: !!config.globalContext,
        debug: config.debug,
        hasConversation: !!config.conversation,
        hasEvaluation: !!config.evaluation,
        hasDatabase: !!config.database,
        deploymentTarget: config.globalContext?.deploymentTarget,
        environment: config.globalContext?.environment,
    });

    // Create event bus
    eventBus = new AnalyticsEventBus(config);

    const registeredProviders: string[] = [];
    const skippedProviders: string[] = [];
    const providerConfigs: Record<string, unknown> = {};

    // Register debug provider in development
    if (config.debug || config.globalContext?.environment === 'development') {
        try {
            debugProvider = new DebugProvider();
            await eventBus.registerProvider(debugProvider);
            registeredProviders.push('debug');
            providerConfigs.debug = {
                enabled: true,
                reason: config.debug ? 'debug=true' : 'development environment',
            };
            logger.debug('✅ Debug provider registered');
        } catch (error) {
            logger.error('❌ Failed to register debug provider:', error);
            skippedProviders.push('debug (error)');
        }
    } else {
        skippedProviders.push('debug');
        providerConfigs.debug = { enabled: false, reason: 'not in debug mode or development' };
    }

    // Always register PostHog provider for UI analytics
    try {
        posthogProvider = new PostHogProvider(config.posthog);
        await eventBus.registerProvider(posthogProvider);
        registeredProviders.push('posthog');
        providerConfigs.posthog = { enabled: true, reason: 'always registered for UI analytics' };
        logger.debug('✅ PostHog provider registered');
    } catch (error) {
        logger.error('❌ Failed to register PostHog provider:', error);
        skippedProviders.push('posthog (error)');
    }

    // Always register Sentry provider for error tracking context sync
    try {
        sentryProvider = new SentryProvider();
        await eventBus.registerProvider(sentryProvider);
        registeredProviders.push('sentry');
        providerConfigs.sentry = {
            enabled: true,
            reason: 'always registered for Sentry context sync',
        };
        logger.debug('✅ Sentry provider registered');
    } catch (error) {
        logger.error('❌ Failed to register Sentry provider:', error);
        skippedProviders.push('sentry (error)');
    }

    // Always register LocalStorage provider for context persistence
    try {
        localStorageProvider = new LocalStorageProvider();
        await eventBus.registerProvider(localStorageProvider);
        registeredProviders.push('localstorage');
        providerConfigs.localstorage = {
            enabled: true,
            reason: 'always registered for context persistence',
        };
        logger.debug('✅ LocalStorage provider registered');
    } catch (error) {
        logger.error('❌ Failed to register LocalStorage provider:', error);
        skippedProviders.push('localstorage (error)');
    }

    // Always register Cookie provider for cross-subdomain session tracking
    try {
        cookieProvider = new CookieProvider();
        await eventBus.registerProvider(cookieProvider);
        registeredProviders.push('cookie');
        providerConfigs.cookie = {
            enabled: true,
            reason: 'always registered for cross-subdomain session tracking',
        };
        logger.debug('✅ Cookie provider registered');
    } catch (error) {
        logger.error('❌ Failed to register Cookie provider:', error);
        skippedProviders.push('cookie (error)');
    }

    // Always register Parent Window provider for postMessage event forwarding (enabled by default)
    try {
        parentWindowProvider = new ParentWindowProvider({
            enabled: true,
            targetOrigin: '*',
            eventCategories: [
                EventCategories.USER_ACTION,
                EventCategories.CONVERSION,
                EventCategories.SYSTEM,
            ],
        });
        await eventBus.registerProvider(parentWindowProvider);
        registeredProviders.push('parent-window');
        providerConfigs['parent-window'] = {
            enabled: true,
            reason: 'always registered for postMessage event forwarding',
        };
        logger.debug('✅ Parent window provider registered');
    } catch (error) {
        logger.error('❌ Failed to register Parent window provider:', error);
        skippedProviders.push('parent-window (error)');
    }

    // Register N8N conversation provider if configured
    if (config.conversation) {
        try {
            n8nProvider = new N8nConversationProvider(config.conversation);
            await eventBus.registerProvider(n8nProvider);
            registeredProviders.push('n8n-conversation');
            providerConfigs['n8n-conversation'] = {
                enabled: true,
                reason: 'conversation config provided',
                config: {
                    conversationUrl: config.conversation.conversationUrl
                        ? '[PROVIDED]'
                        : '[MISSING]',
                    siteName: config.conversation.siteName,
                },
            };
            logger.debug('✅ N8N conversation provider registered');
        } catch (error) {
            logger.error('❌ Failed to register N8N conversation provider:', error);
            skippedProviders.push('n8n-conversation (error)');
        }
    } else {
        skippedProviders.push('n8n-conversation');
        providerConfigs['n8n-conversation'] = {
            enabled: false,
            reason: 'no conversation config provided',
        };
    }

    // Register evaluation provider if configured
    if (config.evaluation) {
        try {
            evaluationProvider = new EvaluationProvider(config.evaluation);
            await eventBus.registerProvider(evaluationProvider);
            registeredProviders.push('evaluation');
            providerConfigs.evaluation = {
                enabled: true,
                reason: 'evaluation config provided',
                config: {
                    evaluationUrl: config.evaluation.evaluationUrl,
                    siteName: config.evaluation.siteName,
                },
            };
            logger.debug('✅ Evaluation provider registered');
        } catch (error) {
            logger.error('❌ Failed to register evaluation provider:', error);
            skippedProviders.push('evaluation (error)');
        }
    } else {
        skippedProviders.push('evaluation');
        providerConfigs.evaluation = { enabled: false, reason: 'no evaluation config provided' };
    }

    // Register database provider if configured
    if (config.database) {
        try {
            databaseProvider = new DatabaseProvider(config.database);
            await eventBus.registerProvider(databaseProvider);
            registeredProviders.push('database');
            providerConfigs.database = {
                enabled: true,
                reason: 'database config provided',
                config: {
                    endpoint: config.database.endpoint ? '[PROVIDED]' : '[MISSING]',
                    apiKey: config.database.apiKey ? '[PROVIDED]' : '[MISSING]',
                },
            };
            logger.debug('✅ Database provider registered');
        } catch (error) {
            logger.error('❌ Failed to register database provider:', error);
            skippedProviders.push('database (error)');
        }
    } else {
        skippedProviders.push('database');
        providerConfigs.database = { enabled: false, reason: 'no database config provided' };
    }

    // Log comprehensive initialization summary
    logger.debug('🎯 Analytics initialization complete:', {
        registeredProviders,
        skippedProviders,
        totalRegistered: registeredProviders.length,
        totalSkipped: skippedProviders.length,
    });

    // Log detailed provider status
    logger.debug('📊 Provider configuration summary:', providerConfigs);

    // Log which event categories will be handled
    const expectedCategories = [];
    if (registeredProviders.includes('debug')) expectedCategories.push('ALL (debug mode)');
    if (registeredProviders.includes('posthog'))
        expectedCategories.push('USER_ACTION, SYSTEM, CONVERSION, NAVIGATION');
    if (registeredProviders.includes('n8n-conversation')) expectedCategories.push('CONVERSATION');
    if (registeredProviders.includes('evaluation')) expectedCategories.push('EVALUATION');
    if (registeredProviders.includes('database')) expectedCategories.push('DATABASE');

    logger.debug('🏷️ Event categories that will be handled:', expectedCategories);

    // Initialize form tracking if enabled
    // NOTE: Site config should be loaded BEFORE analytics initialization
    // so that form detection strategies can access site config for CTA page detection
    if (config.enableFormTracking) {
        logger.debug('🔍 Auto-enabling form tracking (enableFormTracking=true)');
        setupFormTracking({
            domain: config.globalContext?.domain,
        });
    }

    logger.debug('✅ Analytics system initialized successfully');
}

/**
 * Wait for all analytics providers to be ready
 *
 * STRICT MODE: This function will throw an error if PostHog is not ready
 * within the timeout period. PostHog is REQUIRED for the widget to function.
 *
 * @param timeout - Maximum time to wait in milliseconds (default: 5000)
 * @returns Promise that resolves when all providers are ready
 * @throws Error if analytics not initialized or PostHog not ready after timeout
 */
export async function waitForAnalyticsReady(timeout: number = 5000): Promise<void> {
    if (!eventBus) {
        throw new Error('Analytics system not initialized. Call initializeAnalytics() first.');
    }

    logger.debug('⏳ Waiting for analytics providers to be ready...');
    const startTime = Date.now();
    const readinessStatus: Record<string, boolean> = {};

    // Check which providers are registered
    const registeredProviders = eventBus.getProviderNames();
    logger.debug('📋 Registered providers:', registeredProviders);

    // All providers except PostHog are ready immediately
    if (debugProvider) readinessStatus.debug = true;
    if (localStorageProvider) readinessStatus.localstorage = true;
    if (cookieProvider) readinessStatus.cookie = true;
    if (n8nProvider) readinessStatus['n8n-conversation'] = true;
    if (evaluationProvider) readinessStatus.evaluation = true;
    if (databaseProvider) readinessStatus.database = true;

    // Wait for PostHog to be ready (if registered)
    let posthogReady = false;
    if (posthogProvider) {
        readinessStatus.posthog = false;

        while (!posthogProvider.getPostHog()) {
            if (Date.now() - startTime > timeout) {
                const error = `PostHog provider failed to initialize within timeout (${timeout}ms). PostHog is required for widget operation.`;
                logger.error('❌', error);
                throw new Error(error);
            }
            await new Promise((resolve) => setTimeout(resolve, 50));
        }

        posthogReady = !!posthogProvider.getPostHog();
        readinessStatus.posthog = posthogReady;

        if (posthogReady) {
            logger.debug('✅ PostHog provider is ready');
        } else {
            // This should not happen (we throw above), but defensive check
            const error = 'PostHog provider is not ready after timeout';
            logger.error('❌', error);
            throw new Error(error);
        }
    } else {
        // STRICT MODE: PostHog provider must be registered
        const error = 'PostHog provider is required but not registered';
        logger.error('❌', error);
        throw new Error(error);
    }

    // Log final readiness status
    const readyProviders = Object.entries(readinessStatus)
        .filter(([, ready]) => ready)
        .map(([name]) => name);
    const notReadyProviders = Object.entries(readinessStatus)
        .filter(([, ready]) => !ready)
        .map(([name]) => name);

    logger.debug('🎯 Provider readiness summary:', {
        ready: readyProviders,
        notReady: notReadyProviders,
        totalReady: readyProviders.length,
        totalNotReady: notReadyProviders.length,
        waitTime: `${Date.now() - startTime}ms`,
    });

    // STRICT MODE: Throw error if any provider (especially PostHog) is not ready
    if (notReadyProviders.length > 0 && notReadyProviders.includes('posthog')) {
        const error = `PostHog is required but not ready: ${notReadyProviders.join(', ')}`;
        logger.error('❌', error);
        throw new Error(error);
    }

    if (notReadyProviders.length > 0) {
        logger.warn('⚠️ Some non-critical providers are not ready:', notReadyProviders);
    } else {
        logger.debug('✅ All analytics providers are ready');
    }
}

/**
 * Register N8N conversation provider (or update config if already registered)
 */
export async function registerN8nProvider(config: ConversationConfig): Promise<void> {
    if (!eventBus) {
        logger.error('Cannot register N8N provider: Analytics system not initialized');
        throw new Error('Analytics system not initialized. Call initializeAnalytics() first.');
    }

    if (n8nProvider) {
        // Update existing provider's configuration
        if (n8nProvider.updateConfig) {
            n8nProvider.updateConfig(config);
        }
        logger.debug('N8N conversation provider configuration updated');
        return;
    }

    n8nProvider = new N8nConversationProvider(config);
    await eventBus.registerProvider(n8nProvider);
    logger.debug('N8N conversation provider registered');
}

/**
 * Register Evaluation provider (or update config if already registered)
 */
export async function registerEvaluationProvider(config: EvaluationConfig): Promise<void> {
    if (!eventBus) {
        logger.error('Cannot register evaluation provider: Analytics system not initialized');
        throw new Error('Analytics system not initialized. Call initializeAnalytics() first.');
    }

    if (evaluationProvider) {
        // Update existing provider's configuration
        if (evaluationProvider.updateConfig) {
            evaluationProvider.updateConfig(config);
        }
        logger.debug('Evaluation provider configuration updated');
        return;
    }

    evaluationProvider = new EvaluationProvider(config);
    await eventBus.registerProvider(evaluationProvider);
    logger.debug('Evaluation provider registered');
}

/**
 * Register Database provider for future use
 */
export async function registerDatabaseProvider(config: DatabaseConfig): Promise<void> {
    if (!eventBus) {
        throw new Error('Analytics system not initialized. Call initializeAnalytics() first.');
    }

    if (databaseProvider) {
        logger.warn('Database provider already registered');
        return;
    }

    databaseProvider = new DatabaseProvider(config);
    await eventBus.registerProvider(databaseProvider);

    logger.debug('Database provider registered');
}

/**
 * Configuration for registering parent window provider
 * This interface maps from SiteConfigManager's ParentWindowEventsConfig to the provider's ParentWindowConfig
 */
export interface RegisterParentWindowConfig {
    enabled: boolean;
    targetOrigin?: string;
    eventCategories?: string[];
}

/**
 * Register Parent Window provider for cross-domain iframe communication
 * This provider forwards analytics events to the parent window via postMessage()
 *
 * @param config - Configuration from site config (parent_window_events)
 */
export async function registerParentWindowProvider(
    config: RegisterParentWindowConfig
): Promise<void> {
    if (!eventBus) {
        throw new Error('Analytics system not initialized. Call initializeAnalytics() first.');
    }

    if (parentWindowProvider) {
        // Update existing provider's configuration
        parentWindowProvider.updateConfig({
            enabled: config.enabled,
            targetOrigin: config.targetOrigin,
            eventCategories: config.eventCategories as EventCategory[] | undefined,
        });
        logger.debug('🪟 Parent window provider configuration updated');
        return;
    }

    // Convert string categories to EventCategory type
    const providerConfig: ParentWindowConfig = {
        enabled: config.enabled,
        targetOrigin: config.targetOrigin,
        eventCategories: config.eventCategories as EventCategory[] | undefined,
    };

    parentWindowProvider = new ParentWindowProvider(providerConfig);
    await eventBus.registerProvider(parentWindowProvider);

    logger.debug('🪟 Parent window provider registered:', {
        targetOrigin: config.targetOrigin || '*',
        eventCategories: config.eventCategories || ['user_action', 'conversion'],
    });
}

/**
 * Update global analytics context
 */
export function setAnalyticsContext(context: Partial<AnalyticsContext>): void {
    logger.debug('🔧 setAnalyticsContext called with:', context, 'eventBus exists:', !!eventBus);

    if (!eventBus) {
        logger.warn('⚠️ Analytics system not initialized. Context update ignored.', context);
        return;
    }

    logger.debug('✅ Updating analytics context via eventBus');
    eventBus.updateGlobalContext(context);
}

/**
 * Get current global analytics context
 */
export function getAnalyticsContext(): AnalyticsContext {
    if (!eventBus) {
        return {};
    }

    return eventBus.getGlobalContext();
}

/**
 * Check if analytics is properly initialized
 */
export function isAnalyticsInitialized(): boolean {
    return eventBus !== null;
}

/**
 * Set analytics initialization status (for compatibility)
 */
export function setAnalyticsInitialized(initialized: boolean): void {
    // This is mainly for compatibility with old analytics.ts
    // The actual initialization is handled by initializeAnalytics
    if (!initialized && eventBus) {
        // If someone is trying to set it as not initialized, we could destroy the event bus
        // but for now, just log a warning
        logger.warn('setAnalyticsInitialized(false) called but eventBus exists');
    }
}

/**
 * Main tracking function - routes events to appropriate providers
 */
export async function track(
    type: string,
    properties: Record<string, unknown> = {},
    category?:
        | 'user_action'
        | 'system'
        | 'conversion'
        | 'conversation'
        | 'navigation'
        | 'evaluation'
): Promise<void> {
    if (!eventBus) {
        logger.warn(`Analytics system not initialized. Event ${type} ignored.`);
        return;
    }

    await eventBus.track(type, properties, category);
}

/**
 * Unified Analytics API that matches the original ChatAnalytics interface
 * This provides backward compatibility while routing through the new system
 */
export const Analytics = {
    // Widget impression - track when called (components handle deduplication)
    trackWidgetImpression: async (properties: { siteName: string }) => {
        await track(EventTypes.WIDGET_IMPRESSION, properties, EventCategories.SYSTEM);
    },

    // Track when user types something into input (replaces auto-capture)
    trackTypedSomethingIntoInput: async (properties?: {
        inputLength: number;
        isFirstInput?: boolean;
    }) => {
        await track(EventTypes.TYPED_INTO_INPUT, properties, EventCategories.USER_ACTION);
    },

    // Track when user sends a message (with message number to identify first, second, etc.)
    trackMessageSent: async (properties: {
        messageNumber: number;
        messageLength: number;
        message_source?: MessageSourceType;
        conversation_triggered_by?: MessageSourceType;
    }) => {
        await track(EventTypes.MESSAGE_SENT, properties, EventCategories.USER_ACTION);
    },

    trackCTAPageDisplay: async (properties: {
        pageUrl: string;
        source?: 'page_load' | 'cta_click'; // Indicates whether this is actual arrival or pre-navigation intent
    }) => {
        await track(EventTypes.CTA_PAGE_DISPLAY, properties, EventCategories.CONVERSION);
    },

    trackCTARendered: async (properties: {
        language?: string;
        url?: string;
        label?: string;
        placement?: 'search_bar' | 'inline';
        isCtaPage?: boolean;
        hadDemoOffer?: boolean;
        inlineCtaOptionEnabled?: boolean;
    }) => {
        await track(EventTypes.CTA_RENDERED, properties, EventCategories.SYSTEM);
    },

    // CTA clicked - conversion event (user took action on CTA)
    trackCTAClicked: async (properties: {
        language?: string;
        url?: string;
        label?: string;
        ctaId?: string;
        placement?: 'search_bar' | 'inline';
        isCtaPage?: boolean;
        hadDemoOffer?: boolean;
        inlineCtaOptionEnabled?: boolean;
        bookingFlowStarted?: boolean;
        emailAlreadyKnown?: boolean;
        emailPreFilled?: boolean;
    }) => {
        await track(EventTypes.CTA_CLICKED, properties, EventCategories.CONVERSION);
    },

    // Client's OWN website CTA clicked (signup/demo link), matched by destination
    // URL. Lets clients see the out-of-Rose-funnel conversion path (IX-3617).
    trackClientCtaClicked: async (properties: {
        destinationUrl: string;
        ctaLabel?: string;
        pageUrl?: string;
    }) => {
        await track(EventTypes.CLIENT_CTA_CLICKED, properties, EventCategories.CONVERSION);
    },

    // Form submission - conversion event (user submitted a form)
    trackDemoFormSubmitted: async (properties: {
        siteName?: string;
        formId?: string;
        formClass?: string;
        pageUrl?: string;
        formAction?: string;
        formMethod?: string;
        fieldCount?: number;
        detectionMethod?: string;
        provider?: string;
        status: 'success' | 'failed' | 'unknown';
        statusCode?: number;
        errorMessage?: string;
        indicators?: string[];
    }) => {
        await track(EventTypes.FORM_SUBMITTED, properties, EventCategories.CONVERSION);
    },

    trackFormSubmissionFilteredOut: async (properties: {
        siteName?: string;
        formId?: string;
        formClass?: string;
        pageUrl?: string;
        formAction?: string;
        formMethod?: string;
        fieldCount?: number;
        detectionMethod?: string;
        provider?: string;
        status: 'success' | 'failed' | 'unknown';
        statusCode?: number;
        errorMessage?: string;
        indicators?: string[];
        originalEventName: 'client_form_submitted';
        filterReason: 'failed_conversion_filter';
        filteredOut: true;
    }) => {
        await track(EventTypes.FORM_SUBMISSION_FILTERED_OUT, properties, EventCategories.SYSTEM);
    },

    // Track when user clicks the hidden zone to reveal widget
    trackClickHiddenZone: async (properties?: {
        clickMethod?: string; // 'click' | 'keyboard'
        hiddenZoneType?: string; // 'shadow_dom' | 'internal'
    }) => {
        await track(EventTypes.CLICK_HIDDEN_ZONE, properties, EventCategories.USER_ACTION);
    },

    // Track when user hovers over the widget (first time only)
    trackMouseHover: async (properties?: {
        widgetState?: string; // 'collapsed' | 'expanded' | 'minimized'
    }) => {
        await track(EventTypes.MOUSE_HOVER, properties, EventCategories.USER_ACTION);
    },

    // Track when widget is revealed by scrolling on small viewports
    trackWidgetRevealedByScroll: async (properties: {
        viewport_height: number;
        scroll_position: number;
        page_can_scroll: boolean;
    }) => {
        await track(EventTypes.WIDGET_REVEALED_BY_SCROLL, properties, EventCategories.USER_ACTION);
    },

    // Track when dynamic question is clicked
    trackDynamicQuestionClicked: async (properties: {
        questionText: string;
        language?: string;
    }) => {
        await track(EventTypes.DYNAMIC_QUESTION_CLICKED, properties, EventCategories.USER_ACTION);
    },

    // Track when user minimizes dynamic questions
    trackMinimizedDynamicQuestions: async (properties: {
        isMinimized: boolean;
        totalQuestions: number;
        language?: string;
    }) => {
        await track(
            EventTypes.MINIMIZED_DYNAMIC_QUESTIONS,
            properties,
            EventCategories.USER_ACTION
        );
    },

    // Track when an AI Section button or search bar is used
    trackAISectionClicked: async (properties: {
        questionText: string;
        source: 'button' | 'search_bar';
        sectionId?: string;
        language?: string;
    }) => {
        await track(EventTypes.AI_SECTION_CLICKED, properties, EventCategories.USER_ACTION);
    },

    // Track when an AI Section is displayed
    trackAISectionDisplayed: async (properties: {
        questionCount: number;
        questions: string[];
        sectionId?: string;
        hasSearchBar?: boolean;
        language?: string;
    }) => {
        await track(EventTypes.AI_SECTION_DISPLAYED, properties, EventCategories.SYSTEM);
    },

    // Track when follow-up questions list is displayed (once per list, not per question)
    trackFollowUpQuestionsDisplayed: async (properties: {
        questionCount: number;
        questions: string[];
        language?: string;
    }) => {
        await track(EventTypes.FOLLOW_UP_QUESTIONS_DISPLAYED, properties, EventCategories.SYSTEM);
    },

    // Track when a specific follow-up question is clicked
    trackFollowUpQuestionClicked: async (properties: {
        questionText: string;
        questionIndex: number;
        language?: string;
    }) => {
        await track(EventTypes.FOLLOW_UP_QUESTION_CLICKED, properties, EventCategories.USER_ACTION);
    },

    // Track when suggested answers list is displayed (once per list, not per answer)
    trackSuggestedAnswersDisplayed: async (properties: {
        answerCount: number;
        answers: string[];
        language?: string;
    }) => {
        await track(EventTypes.SUGGESTED_ANSWERS_DISPLAYED, properties, EventCategories.SYSTEM);
    },

    // Track when a specific suggested answer is clicked
    trackSuggestedAnswerClicked: async (properties: {
        answerText: string;
        answerIndex: number;
        language?: string;
    }) => {
        await track(EventTypes.SUGGESTED_ANSWER_CLICKED, properties, EventCategories.USER_ACTION);
    },

    // Track when a content recommendation is displayed (once per URL, both internal and external)
    trackContentRecommendationDisplayed: async (properties: {
        title: string;
        url: string;
        category: string;
        is_internal: boolean;
    }) => {
        await track(
            EventTypes.CONTENT_RECOMMENDATION_DISPLAYED,
            properties,
            EventCategories.SYSTEM
        );
    },

    // Track when a content recommendation is clicked (both internal link and external card)
    trackContentRecommendationClicked: async (properties: {
        title: string;
        url: string;
        category: string;
        is_internal: boolean;
    }) => {
        await track(
            EventTypes.CONTENT_RECOMMENDATION_CLICKED,
            properties,
            EventCategories.USER_ACTION
        );
    },

    // Track PostHog initialization with display decision data
    // Components handle deduplication - this function just tracks when called
    trackPostHogInitialized: async (properties?: {
        deploymentTarget?: string;
        apiHost?: string;
        // Display decision core
        displayAllowed?: boolean;
        displayReason?: string;
        forceDisplay?: boolean;
        // Traffic control (Layer 1)
        trafficControlPassed?: boolean;
        trafficControlReason?: string;
        userBucket?: number;
        // Display rules (Layer 2)
        urlAllowed?: boolean;
        deviceAllowed?: boolean;
        currentUrl?: string;
        // Device detection
        isMobile?: boolean;
        mobileEnabled?: boolean;
        // Configuration
        excludePatterns?: string[];
        // Experiment cohorts
        rw_staff_experiment_id?: string;
        rw_staff_experiment_start_date?: string;
        rw_staff_experiment_assignment?: 'control' | 'treatment';
        rw_client_experiment_id?: string;
        rw_client_experiment_start_date?: string;
        rw_client_experiment_assignment?: 'control' | 'treatment';
        rw_rose_exposure_variant?: 'control' | 'treatment';
        rw_rose_exposure_reason?: 'staff_holdout' | 'client_experiment' | 'none';
    }) => {
        await track(EventTypes.POSTHOG_INITIALIZED, properties, EventCategories.SYSTEM);
    },

    // Track login clicks - navigation event that needs guaranteed delivery
    trackLoginClicked: async (properties: { loginUrl: string }) => {
        await track(EventTypes.LOGIN_CLICKED, properties, EventCategories.NAVIGATION);
    },

    // Track conversation data (for N8N provider)
    trackConversation: async (properties: {
        query: string;
        messageId: string;
        conversation_history: Array<{
            role: string;
            content: string;
            timestamp: string;
        }>;
        output: string;
        sources?: Array<{
            title: string;
            url: string;
            snippet: string;
        }>;
        sources_count: number;
        has_sources: boolean;
        threadId?: string;
        message_source?: MessageSourceType;
        visitor_profile?: VisitorProfile | null;
    }) => {
        await track(EventTypes.CONVERSATION_TRACKED, properties, EventCategories.CONVERSATION);
    },

    // Evaluation tracking methods
    trackEvaluationSubmitted: async (properties: {
        rating:
            | 'Accurate & OK'
            | 'Accurate but Not OK'
            | 'Not Accurate but OK'
            | 'Not Accurate & Not OK'
            | 'thumbs_up'
            | 'thumbs_down';
        comment?: string;
        messageText: string;
        messageId: string;
        url?: string;
        pageTitle?: string;
        threadId?: string;
    }) => {
        await track(EventTypes.EVALUATION_SUBMITTED, properties, EventCategories.EVALUATION);
    },

    trackEvaluationPopupShown: async (properties: {
        messageId: string;
        rating: 'thumbs_up' | 'thumbs_down';
    }) => {
        await track(EventTypes.EVALUATION_POPUP_SHOWN, properties, EventCategories.EVALUATION);
    },

    trackEvaluationPopupCancelled: async (properties: {
        messageId: string;
        rating: 'thumbs_up' | 'thumbs_down';
    }) => {
        await track(EventTypes.EVALUATION_POPUP_CANCELLED, properties, EventCategories.EVALUATION);
    },

    trackEvaluationThumbsClicked: async (properties: {
        messageId: string;
        rating: 'thumbs_up' | 'thumbs_down';
    }) => {
        await track(EventTypes.EVALUATION_THUMBS_CLICKED, properties, EventCategories.EVALUATION);
    },

    // Post-qualification funnel events
    trackPostQualificationStarted: async (properties: {
        totalQuestions: number;
        pageUrl: string;
    }) => {
        await track(EventTypes.POST_QUALIFICATION_STARTED, properties, EventCategories.SYSTEM);
    },

    trackPostQualificationQuestionDisplayed: async (properties: {
        questionNumber: number;
        totalQuestions: number;
        progressPercent: number;
    }) => {
        await track(
            EventTypes.POST_QUALIFICATION_QUESTION_DISPLAYED,
            properties,
            EventCategories.SYSTEM
        );
    },

    trackPostQualificationAnswerSubmitted: async (properties: {
        questionNumber: number;
        totalQuestions: number;
        answerSource: 'suggested_answer' | 'user_typed';
    }) => {
        await track(
            EventTypes.POST_QUALIFICATION_ANSWER_SUBMITTED,
            properties,
            EventCategories.USER_ACTION
        );
    },

    trackPostQualificationCompleted: async (properties: {
        totalQuestions: number;
        messageCount: number;
    }) => {
        await track(
            EventTypes.POST_QUALIFICATION_COMPLETED,
            properties,
            EventCategories.CONVERSION
        );
    },

    trackPostQualificationDismissed: async (properties: {
        questionNumber: number;
        totalQuestions: number;
        progressPercent: number;
    }) => {
        await track(
            EventTypes.POST_QUALIFICATION_DISMISSED,
            properties,
            EventCategories.USER_ACTION
        );
    },
};

/**
 * Enable/disable specific providers
 */
export function setProviderEnabled(providerName: string, enabled: boolean): void {
    if (!eventBus) {
        logger.warn('Analytics system not initialized. Provider control ignored.');
        return;
    }

    eventBus.setProviderEnabled(providerName, enabled);
}

/**
 * Get list of registered providers
 */
export function getRegisteredProviders(): string[] {
    if (!eventBus) {
        return [];
    }

    return eventBus.getProviderNames();
}

/**
 * Get detailed analytics status including provider readiness
 */
export function getAnalyticsStatus(): {
    initialized: boolean;
    eventBusReady: boolean;
    providers: Record<
        string,
        {
            registered: boolean;
            ready: boolean;
            type: string;
            config?: unknown;
        }
    >;
    summary: {
        totalProviders: number;
        readyProviders: number;
        registeredProviders: string[];
        readyProviderNames: string[];
    };
} {
    if (!eventBus) {
        return {
            initialized: false,
            eventBusReady: false,
            providers: {},
            summary: {
                totalProviders: 0,
                readyProviders: 0,
                registeredProviders: [],
                readyProviderNames: [],
            },
        };
    }

    const providerStatus: Record<
        string,
        { registered: boolean; ready: boolean; type: string; config?: unknown }
    > = {};

    // Debug provider
    providerStatus.debug = {
        registered: !!debugProvider,
        ready: !!debugProvider,
        type: 'logging',
    };

    // PostHog provider
    providerStatus.posthog = {
        registered: !!posthogProvider,
        ready: !!posthogProvider?.getPostHog(),
        type: 'analytics',
    };

    // LocalStorage provider
    providerStatus.localstorage = {
        registered: !!localStorageProvider,
        ready: !!localStorageProvider,
        type: 'persistence',
    };

    // Cookie provider
    providerStatus.cookie = {
        registered: !!cookieProvider,
        ready: !!cookieProvider,
        type: 'persistence',
    };

    // N8N conversation provider
    providerStatus['n8n-conversation'] = {
        registered: !!n8nProvider,
        ready: !!n8nProvider,
        type: 'conversation_tracking',
    };

    // Evaluation provider
    providerStatus.evaluation = {
        registered: !!evaluationProvider,
        ready: !!evaluationProvider,
        type: 'evaluation',
    };

    // Database provider
    providerStatus.database = {
        registered: !!databaseProvider,
        ready: !!databaseProvider,
        type: 'database',
    };

    // Parent window provider
    providerStatus['parent-window'] = {
        registered: !!parentWindowProvider,
        ready: !!parentWindowProvider,
        type: 'event_forwarding',
    };

    const registeredProviders = Object.entries(providerStatus)
        .filter(([, status]) => status.registered)
        .map(([name]) => name);

    const readyProviders = Object.entries(providerStatus)
        .filter(([, status]) => status.ready)
        .map(([name]) => name);

    return {
        initialized: true,
        eventBusReady: true,
        providers: providerStatus,
        summary: {
            totalProviders: Object.keys(providerStatus).length,
            readyProviders: readyProviders.length,
            registeredProviders,
            readyProviderNames: readyProviders,
        },
    };
}

/**
 * Destroy the analytics system and clean up resources
 */
export async function destroyAnalytics(): Promise<void> {
    if (eventBus) {
        await eventBus.destroy();
        eventBus = null;
    }

    posthogProvider = null;
    localStorageProvider = null;
    cookieProvider = null;
    n8nProvider = null;
    evaluationProvider = null;
    debugProvider = null;
    databaseProvider = null;
    parentWindowProvider = null;

    logger.debug('Analytics system destroyed');
}

// CTA and analytics configuration utilities (from original posthog.ts)
interface CTAConfig {
    placeholder: string;
    langs: Record<string, { url: string; text: string }>;
}

export const setCTAConfiguration = (ctas: CTAConfig[] | null) => {
    // Configuration passed through to PostHog provider if available
    if (posthogProvider && 'setCTAConfiguration' in posthogProvider) {
        // biome-ignore lint/suspicious/noExplicitAny: setCTAConfiguration is checked via 'in' operator but not in the provider interface
        (posthogProvider as any).setCTAConfiguration(ctas);
    }
    // Note: Form rescanning is no longer needed - analytics initialization now happens
    // in ConfigProvider after config is fully loaded
};

export const setAnalyticsConfiguration = (analytics: { login_urls?: string[] } | null) => {
    // Configuration passed through to PostHog provider if available
    if (posthogProvider && 'setAnalyticsConfiguration' in posthogProvider) {
        // biome-ignore lint/suspicious/noExplicitAny: setAnalyticsConfiguration is checked via 'in' operator but not in the provider interface
        (posthogProvider as any).setAnalyticsConfiguration(analytics);
    }
};

/**
 * Set PostHog instance from React component
 * @param posthog - PostHog instance from usePostHog hook
 */
export const setPostHogInstance = (posthog: unknown | null): void => {
    if (posthogProvider && 'setPostHogInstance' in posthogProvider) {
        // biome-ignore lint/suspicious/noExplicitAny: setPostHogInstance accepts the raw PostHog SDK instance
        (posthogProvider as any).setPostHogInstance(posthog);
    }
};

// PostHog utility for external access (mainly for dynamic question manager)
export const getPostHog = (): unknown | null => {
    if (posthogProvider) {
        // Access the provider's getPostHog method if available
        return posthogProvider.getPostHog?.() || null;
    }
    return null;
};

export const hasPostHogInitializedEventBeenSent = (): boolean => {
    if (!posthogProvider) {
        return false;
    }

    return posthogProvider.hasSentPostHogInitializedEvent();
};

/**
 * Emit the `$pageview` that posthog-js skipped because the page-load event policy
 * suppressed it at init. Only relevant after SPA navigation from an out-of-scope host
 * onto an in-scope page. No-op otherwise, and at most once per page load.
 */
export const capturePageviewOnce = (): void => {
    posthogProvider?.capturePageviewOnce();
};

// Public payload type for trackClientFormSubmission / window.InboundXWidget.trackFormSubmitted
export type { FormTrackingData } from './formDetection';
// Re-export person ID utilities for external use
export {
    getPersonId,
    hasPostHogDistinctId,
} from './person-id';
export type { AnalyticsContext, AnalyticsEvent, AnalyticsProvider } from './types';
// Re-export types and constants for convenience
export { EventCategories, EventTypes } from './types';

// Cookie utilities for reading cross-subdomain data
/**
 * Get the last active session ID from cookie
 * This cookie works across all subdomains
 */
export function getCookieLastActiveSessionId(): string | null {
    if (cookieProvider) {
        return cookieProvider.getLastActiveSessionId();
    }
    return null;
}

/**
 * Get the client ID from cookie
 * This cookie works across all subdomains
 */
export function getCookieClientId(): string | null {
    if (cookieProvider) {
        return cookieProvider.getClientId();
    }
    return null;
}

/**
 * Get all Rose cookies (useful for debugging)
 */
export function getAllRoseCookies(): {
    lastActiveSessionId: string | null;
    clientId: string | null;
} {
    if (cookieProvider) {
        return cookieProvider.getAllCookies();
    }
    return {
        lastActiveSessionId: null,
        clientId: null,
    };
}

// Form detection and tracking
//
// USAGE:
//
// Option 1: Auto-enable during analytics initialization
//   await initializeAnalytics({ enableFormTracking: true });
//
// Option 2: Manually call when needed (e.g., on CTA pages)
//   import { setupFormTracking } from '@inboundx/shared/analytics';
//   setupFormTracking({ domain: 'example.com' });
//
// WHAT IT DOES:
// - Automatically detects all forms on the page
// - Tracks form submissions via multiple methods (submit event, button clicks)
// - Watches for dynamically added forms
// - Logs detection and submission events for debugging
//
// LOGS YOU'LL SEE:
// - 🔍 Setting up automatic form tracking
// - 📝 Applying N strategies to form (when forms are detected)
// - 🎯 Form submission detected via submit event (when user submits)
// - 📋 Form submission detected, tracking analytics event (before sending to PostHog)
// - 📊 PostHog tracking rw_client_form_submitted (final tracking event)

let formDetectionManager: FormDetectionManager | null = null;

/**
 * Helper function to track form submission only if on a CTA destination page
 * Thank-you page detections always bypass the CTA destination check since they
 * confirm successful submissions and are validated by URL pattern matching.
 * @param domain - Current domain
 * @param data - Form submission data
 */
function trackFormSubmissionIfAllowed(domain: string, rawData: FormTrackingData): boolean {
    // IX-4341: resolve the page URL at submission time. FormDetectionContext.currentUrl
    // is captured once in setupFormTracking and never refreshed, so every strategy that
    // reads context.currentUrl (post-message, dataLayer, network- and DOM-observer, plus
    // the CTA-page/HubSpot/Superform strategies) reports the URL the widget booted on
    // after any SPA navigation. That stale value is both filtered on here and persisted
    // to session_events.page_url, where it feeds the server-side url_exclude backstop and
    // conversion_labels page_url_patterns. ThankYouPageStrategy already reads
    // window.location.href live; this makes every path behave that way.
    const data: FormTrackingData =
        typeof window === 'undefined' ? rawData : { ...rawData, pageUrl: window.location.href };

    // Thank-you page, formless page, post-message, and dataLayer detections should
    // always be tracked — they have their own eligibility checks (URL pattern
    // matching, post_request_capture patterns, trusted iframe origins, or an
    // explicit dataLayer event-name allowlist). The event itself is the signal.
    const isThankYouPageDetection = data.detectionMethod === 'thankyou_page_detected';
    const isFormlessPageDetection =
        data.detectionMethod === 'network_observer' || data.detectionMethod === 'dom_observer';
    const isPostMessageDetection = data.detectionMethod === 'post_message';
    const isDataLayerDetection = data.detectionMethod === 'data_layer';

    if (
        !isThankYouPageDetection &&
        !isFormlessPageDetection &&
        !isPostMessageDetection &&
        !isDataLayerDetection &&
        !shouldEnableFormTracking(domain)
    ) {
        logger.debug('⏭️ Skipping form submission tracking (not a CTA destination page)');
        return false;
    }

    // conversion_filters gate. Every axis applies to every detection method;
    // `passesConversionFilter` skips an axis when the submission carries no
    // value for it, so a thank-you or formless detection with no form id is
    // still gated on the URL axes alone, exactly as before (IX-4341).
    //
    // IX-4411: this used to be a per-detection-method relaxation (`urlAxesOnly`)
    // rather than a per-value one, which disabled the whitelist for post-message
    // and dataLayer detections that DO carry a usable form id. Najar's
    // comparison-page HubSpot forms reached PostHog as `rw_client_form_submitted`
    // that way and were announced in Slack as demos, while the server — which
    // has always applied the form-id axis to any row carrying one — filtered
    // them out of every conversion count.
    //
    // The server stays the backstop regardless: widget bundles are cached on
    // client sites, so loose events keep arriving from old builds for weeks.
    if (!passesConversionFilter(domain, data)) {
        logger.debug('⏭️ Skipping form submission (failed conversion_filters)', {
            formId: data.formId,
            detectionMethod: data.detectionMethod,
        });
        Analytics.trackFormSubmissionFilteredOut({
            siteName: domain,
            ...data,
            originalEventName: 'client_form_submitted',
            filterReason: 'failed_conversion_filter',
            filteredOut: true,
        });
        return false;
    }

    logger.debug('📋 Form submission detected, tracking analytics event:', data);
    Analytics.trackDemoFormSubmitted({ siteName: domain, ...data });
    return true;
}

/**
 * Public API: report a host-page form submission to Rose.
 *
 * For clients whose form/submit flow Rose's automatic detection can't reliably
 * observe (SPA, custom JS). Fires `rw_client_form_submitted` directly — bypasses
 * the automatic detection-strategy gating (`form_tracking_pages` /
 * `conversion_filters`), since the explicit client call IS the conversion signal
 * (same rationale as thank-you-page / dataLayer detections).
 *
 * Exposed on the widget as `window.InboundXWidget.trackFormSubmitted(data?)`.
 *
 * @param data Optional form metadata. `status` defaults to `'success'`,
 *             `detectionMethod` to `'client_api'`, `pageUrl` to the current URL.
 * @returns `true` if tracked, `false` if analytics is not ready (no domain yet).
 */
export function trackClientFormSubmission(data?: Partial<FormTrackingData>): boolean {
    const domain = getAnalyticsContext().domain;
    if (!domain) {
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const failure = getConfigLoadFailure(hostname);
        logger.warn(
            failure
                ? `trackClientFormSubmission: conversion not tracked (${failure.type})`
                : 'trackClientFormSubmission: analytics not ready (no domain), ignoring',
            {
                hostname,
                ...(failure && { config_error_type: failure.type, error_source: failure.source }),
            }
        );
        return false;
    }

    const payload: FormTrackingData = {
        status: 'success',
        detectionMethod: 'client_api',
        pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        ...data,
    };

    logger.debug('📋 Client-reported form submission (client_api)', payload);
    Analytics.trackDemoFormSubmitted({ siteName: domain, ...payload });
    return true;
}

/**
 * Synchronous client-CTA click emit for real outbound-link navigations (IX-3617).
 *
 * Unlike Rose's own CTA button (which awaits tracking, then navigates), a click on
 * the client's native CTA link navigates immediately — the browser tears the page
 * down before the async event-bus chain reaches the PostHog capture, so the event is
 * lost (same-tab or cross-origin). This fires the sendBeacon capture SYNCHRONOUSLY,
 * inside the click handler, before navigation — the only reliable way to deliver a
 * navigation-coincident event without preventDefault (which would interfere with the
 * host page's handlers). Fire-and-forget; never throws on the host page.
 */
export function trackClientCtaClickedSync(properties: {
    destinationUrl?: string;
    ctaLabel?: string;
    pageUrl?: string;
}): void {
    posthogProvider?.captureNavigationEventSync(EventTypes.CLIENT_CTA_CLICKED, properties);
}

// Deduplication set for CTA destination page tracking
const trackedCTADestinations = new Set<string>();

/**
 * Track CTA destination page display if current page is a configured destination
 * Uses deduplication to prevent tracking the same page multiple times
 * @param domain - Current domain
 * @param currentUrl - Current page URL
 */
function trackCTADestinationPageIfNeeded(domain: string, currentUrl: string): void {
    // Check if current page is a CTA destination
    if (!isCurrentPageCTADestination(domain, currentUrl)) {
        return;
    }

    // Deduplication: only track each URL once per session
    if (trackedCTADestinations.has(currentUrl)) {
        logger.debug('⏭️ CTA destination page already tracked', { currentUrl });
        return;
    }

    // Mark as tracked
    trackedCTADestinations.add(currentUrl);

    logger.debug('📊 Tracking CTA destination page display', {
        currentUrl,
        source: 'page_load',
    });

    // Track the page display
    Analytics.trackCTAPageDisplay({
        pageUrl: currentUrl,
        source: 'page_load',
    });
}

/**
 * Setup automatic form detection and tracking on the current page
 * This should be called when landing on a CTA page or any page where you want to track form submissions
 *
 * @param options - Configuration options for form tracking
 * @param options.domain - Current domain (e.g., 'toucantoco.com')
 * @param options.enableNetworkInterception - Enable network interception for AJAX forms (default: false, can cause high CPU usage)
 * @param options.initialDelay - Delay in milliseconds before initial form scan (useful for slow-loading forms, default: 0)
 */
export function setupFormTracking(options?: {
    domain?: string;
    enableNetworkInterception?: boolean;
    initialDelay?: number;
}): void {
    // Don't initialize multiple times
    if (formDetectionManager) {
        logger.debug('Form tracking already initialized');
        return;
    }

    const context = getAnalyticsContext();

    if (!context.domain) {
        throw new Error(
            'Cannot initialize form tracking: domain not available in analytics context'
        );
    }

    const domain = context.domain;
    const currentUrl = window.location.href;

    logger.debug('🔍 Setting up automatic form tracking', {
        domain,
        currentUrl,
        enableNetworkInterception: options?.enableNetworkInterception || false,
    });

    // Create form detection manager with per-client strategy toggles
    const strategyToggles = getFormDetectionStrategies(domain);
    const dataLayerConversionEvents = getDataLayerConversionEvents(domain);
    formDetectionManager = new FormDetectionManager(undefined, strategyToggles, {
        dataLayerConversionEvents,
    });

    // Check if current page is a CTA destination and track page display
    trackCTADestinationPageIfNeeded(domain, currentUrl);

    // Start observing for forms (both existing and dynamically added)
    // Use optional initial delay to wait for slow-loading forms
    formDetectionManager.startObserving(
        {
            domain,
            currentUrl,
            onFormSubmit: (data) => {
                if (trackFormSubmissionIfAllowed(domain, data)) {
                    notifyFormSubmit(domain, data);
                }
            },
        },
        {
            initialDelay: options?.initialDelay || 0,
        }
    );

    logger.debug(`✅ Form tracking initialized with automatic observer`);
}

/**
 * Rescan all forms on the page with current configuration
 *
 * This function should be called after site config is loaded or updated
 * (e.g., after runtime config overrides are applied) to ensure forms
 * are properly tracked based on the latest configuration.
 *
 * @example
 * // After loading site config with additional_form_tracking_pages
 * rescanForms();
 */
export function rescanForms(): void {
    if (!formDetectionManager) {
        logger.debug('⚠️ Form tracking not initialized, cannot rescan');
        return;
    }

    const context = getAnalyticsContext();
    if (!context.domain) {
        logger.warn('Cannot rescan forms: domain not available in analytics context');
        return;
    }

    const domain = context.domain;
    const currentUrl = window.location.href;

    logger.debug('🔄 Rescanning forms after config update', {
        domain,
        currentUrl,
    });

    // Rescan all forms with updated config
    const foundForms = formDetectionManager.trackAllForms({
        domain,
        currentUrl,
        onFormSubmit: (data) => {
            trackFormSubmissionIfAllowed(domain, data);
        },
    });

    logger.debug(`✅ Form rescan completed, found ${foundForms} forms`);
}

/**
 * Cleanup form tracking and remove event listeners
 */
export function cleanupFormTracking(): void {
    // Clean up the form detection manager (includes stopping observer)
    if (formDetectionManager) {
        formDetectionManager.cleanup();
        formDetectionManager = null;
        logger.debug('🧹 Form tracking cleaned up');
    }
}
